import styled, { keyframes } from 'styled-components'
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import colors from '../utils/style/colors'
import { stockMeta } from '../utils/stockStatus'
import { supabase } from '../supabase'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`
const bgShift = keyframes`
  from { filter: brightness(1); }
  to   { filter: brightness(1.1) saturate(1.1); }
`

// ─── MINI HERO ───
const MiniHero = styled.section`
  position: relative; height: 180px;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: ${colors.heroBg};
  @media (max-width: 768px) { height: 140px; }
`
const HeroBg = styled.div`
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%, rgba(58,90,46,0.5) 0%, transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%, rgba(107,76,42,0.35) 0%, transparent 60%),
    linear-gradient(160deg, #0e1a0a 0%, #1e2e14 40%, #2a1a0a 100%);
  animation: ${bgShift} 10s ease-in-out infinite alternate;
`
const Grain = styled.div`
  position: absolute; inset: 0; pointer-events: none; opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px 200px;
`
const HeroContent = styled.div`
  position: relative; z-index: 2; text-align: center;
  animation: ${fadeUp} 0.8s both;
`
const Breadcrumb = styled.div`
  font-family: ${colors.font}; font-size: 0.72rem; font-weight: 500;
  color: rgba(255,255,255,0.45); letter-spacing: 0.08em;
  text-transform: uppercase; margin-bottom: 14px;
  a { color: ${colors.orClair}; text-decoration: none; &:hover { text-decoration: underline; } }
`
const HeroTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 900; color: #fff; line-height: 1.1; margin: 0;
  text-shadow: 0 4px 24px rgba(0,0,0,0.5);
  em { font-style: normal; color: ${colors.orClair}; }
`
const HeroBar = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, transparent, ${colors.or}, transparent);
`

// ─── LAYOUT ───
const PageWrap = styled.div`
  background: ${colors.blanc};
  padding: 60px 48px 100px;
  @media (max-width: 768px) { padding: 40px 20px 80px; }
`
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 60px;
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeUp} 0.6s 0.1s both;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 40px; }
`

// ─── COLONNE GAUCHE ───
const Left = styled.div``

const ProductVisual = styled.div`
  height: 360px;
  border-radius: 10px;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
  background: ${(p) =>
    p.$cat === 'Paille' ? 'linear-gradient(135deg, rgba(200,168,75,0.15), #e8d5b8)' :
    p.$cat === 'Foin'   ? 'linear-gradient(135deg, rgba(90,138,69,0.15), #e8f0e4)' :
                          'linear-gradient(135deg, rgba(107,76,42,0.15), #f0e8dc)'};
  border: 1px solid ${colors.beigeFonce};
`
const MainPhoto = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`
const MainIconFallback = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 7rem;
`
const Thumbnails = styled.div`
  display: flex; gap: 10px; margin-bottom: 32px;
`
const Thumb = styled.div`
  width: 72px; height: 72px; border-radius: 6px; overflow: hidden;
  border: 2px solid ${(p) => p.$active ? colors.or : colors.beigeFonce};
  cursor: pointer; flex-shrink: 0;
  transition: border-color 0.2s;
  &:hover { border-color: ${colors.or}; }
  img { width: 100%; height: 100%; object-fit: cover; }
`
const CatBadge = styled.span`
  position: absolute; top: 16px; left: 16px;
  background: ${colors.vertOlive}; color: #fff;
  font-family: ${colors.font}; font-size: 0.68rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 2px;
`
const StockBadge = styled.span`
  position: absolute; top: 16px; right: 16px;
  background: ${(p) => p.$bg}; color: #fff;
  font-family: ${colors.font}; font-size: 0.68rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 2px;
`

const DescText = styled.p`
  font-family: ${colors.font};
  font-size: 0.95rem;
  color: ${colors.texteLeger};
  line-height: 1.7;
  margin: 0 0 32px;
`

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.4rem; font-weight: 700;
  color: ${colors.texte}; margin: 0 0 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${colors.or};
  display: inline-block;
`
const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 36px;
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`
const SpecCard = styled.div`
  background: #fff;
  border: 1px solid ${colors.beigeFonce};
  border-radius: 6px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 4px;
`
const SpecCardLabel = styled.span`
  font-family: ${colors.font}; font-size: 0.68rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase; color: ${colors.texteLeger};
`
const SpecCardVal = styled.span`
  font-family: ${colors.font}; font-size: 0.95rem; font-weight: 600;
  color: ${colors.texte};
`

const UsageSection = styled.div` margin-bottom: 36px; `
const UsageTags = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px;
`
const UsageTag = styled.span`
  background: ${colors.beige}; color: ${colors.texte};
  font-family: ${colors.font}; font-size: 0.82rem; font-weight: 600;
  padding: 6px 14px; border-radius: 20px;
  border: 1px solid ${colors.beigeFonce};
`

const CoupeSection = styled.div` margin-bottom: 36px; `
const CoupeTags = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px;
`
const CoupeTag = styled.span`
  background: rgba(58,90,46,0.08); color: ${colors.vertOlive};
  font-family: ${colors.font}; font-size: 0.82rem; font-weight: 600;
  padding: 6px 14px; border-radius: 20px;
  border: 1px solid rgba(58,90,46,0.2);
`

// ─── COLONNE DROITE (sticky CTA) ───
const Right = styled.div``

const CtaCard = styled.div`
  background: linear-gradient(135deg, ${colors.vertOlive} 0%, #2a4520 100%);
  border-radius: 10px;
  padding: 32px 28px;
  position: sticky;
  top: 84px;
`
const CtaTitle = styled.h3`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.3rem; font-weight: 700; color: #fff;
  margin: 0 0 10px; line-height: 1.3;
`
const CtaDesc = styled.p`
  font-family: ${colors.font}; font-size: 0.88rem;
  color: rgba(255,255,255,0.7); margin: 0 0 24px;
  line-height: 1.6;
`
const CtaBtn = styled(Link)`
  display: block; text-align: center;
  background: ${colors.or}; color: ${colors.texte};
  font-family: ${colors.font}; font-size: 0.88rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  text-decoration: none; padding: 14px 20px; border-radius: 4px;
  margin-bottom: 12px;
  transition: all 0.25s;
  &:hover { background: ${colors.orClair}; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(200,168,75,0.4); }
`
const CtaSecondary = styled(Link)`
  display: block; text-align: center;
  background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.85);
  font-family: ${colors.font}; font-size: 0.82rem; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  text-decoration: none; padding: 12px 20px; border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.2);
  transition: all 0.2s;
  &:hover { background: rgba(255,255,255,0.18); }
`
const CtaDivider = styled.div`
  height: 1px; background: rgba(255,255,255,0.12); margin: 22px 0;
`
const CtaInfoRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  font-family: ${colors.font}; font-size: 0.8rem;
  color: rgba(255,255,255,0.65); margin-bottom: 10px;
  &:last-child { margin-bottom: 0; }
`
const CtaInfoIcon = styled.span` font-size: 1rem; `

const BackLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 6px;
  font-family: ${colors.font}; font-size: 0.78rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: ${colors.texteLeger}; text-decoration: none;
  margin-bottom: 40px;
  transition: color 0.2s;
  &:hover { color: ${colors.or}; }
`

function getIcon(name) {
  if (!name) return '🌾'
  if (name.toLowerCase().includes('luzerne')) return '🌱'
  if (name.toLowerCase().includes('foin') || name.toLowerCase().includes('prairie')) return '🌿'
  return '🌾'
}
function getCat(name) {
  if (!name) return 'Paille'
  if (name.toLowerCase().includes('paille')) return 'Paille'
  if (name.toLowerCase().includes('foin') || name.toLowerCase().includes('luzerne') || name.toLowerCase().includes('prairie')) return 'Foin'
  return 'Autre'
}

function ProduitDetail() {
  const { id } = useParams()
  const [p, setP] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeImg, setActiveImg] = useState(0)

  useEffect(() => {
    let cancelled = false
    const load = async (attempt = 1) => {
      const { data, error } = await supabase
        .from('products')
        .select('*, product_images(*)')
        .eq('id', id)
        .single()
      if (cancelled) return
      if (error && attempt < 3) {
        setTimeout(() => load(attempt + 1), 800 * attempt)
        return
      }
      setP(data)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [id])

  if (loading) return (
    <PageWrap style={{ textAlign: 'center', paddingTop: 120 }}>
      <p style={{ fontFamily: colors.font, color: colors.texteLeger }}>Chargement…</p>
    </PageWrap>
  )

  if (!p) return (
    <PageWrap style={{ textAlign: 'center', paddingTop: 120 }}>
      <p style={{ fontFamily: colors.font, color: colors.texteLeger }}>Produit introuvable.</p>
      <Link to="/produits" style={{ color: colors.or }}>← Retour aux produits</Link>
    </PageWrap>
  )

  const images = p.product_images?.sort((a, b) => a.order_index - b.order_index) || []
  const cat = getCat(p.name)
  const icon = getIcon(p.name)
  const coupes = p.coupes || []
  const usages = p.usages || []

  return (
    <>
      <MiniHero>
        <HeroBg />
        <Grain />
        <HeroContent>
          <Breadcrumb>
            <Link to="/produits">Produits</Link> &nbsp;/&nbsp; {p.name}
          </Breadcrumb>
          <HeroTitle><em>{p.name}</em></HeroTitle>
        </HeroContent>
        <HeroBar />
      </MiniHero>

      <PageWrap>
        <BackLink to="/produits">← Retour aux produits</BackLink>

        <Layout>
          {/* ── GAUCHE ── */}
          <Left>
            <ProductVisual $cat={cat}>
              {images.length > 0
                ? <MainPhoto src={images[activeImg].url} alt={p.name} onError={(e) => { e.target.style.display='none' }} />
                : <MainIconFallback>{icon}</MainIconFallback>
              }
              <CatBadge>{cat}</CatBadge>
              <StockBadge $bg={stockMeta(p).badge}>{stockMeta(p).label}</StockBadge>
            </ProductVisual>
            {images.length > 1 && (
              <Thumbnails>
                {images.map((img, i) => (
                  <Thumb key={i} $active={activeImg === i} onClick={() => setActiveImg(i)}>
                    <img src={img.url} alt={`${p.name} ${i+1}`} />
                  </Thumb>
                ))}
              </Thumbnails>
            )}

            {p.description && (
              <DescText>{p.description}</DescText>
            )}

            <SectionTitle>Caractéristiques</SectionTitle>
            <SpecsGrid>
              {p.format && (
                <SpecCard>
                  <SpecCardLabel>📦 Format</SpecCardLabel>
                  <SpecCardVal>{p.format}</SpecCardVal>
                </SpecCard>
              )}
              {p.dimensions && (
                <SpecCard>
                  <SpecCardLabel>📐 Dimensions</SpecCardLabel>
                  <SpecCardVal>{p.dimensions} cm</SpecCardVal>
                </SpecCard>
              )}
              {p.poids && (
                <SpecCard>
                  <SpecCardLabel>⚖️ Poids moyen</SpecCardLabel>
                  <SpecCardVal>{p.poids}</SpecCardVal>
                </SpecCard>
              )}
              <SpecCard>
                <SpecCardLabel>🇫🇷 Origine</SpecCardLabel>
                <SpecCardVal>France</SpecCardVal>
              </SpecCard>
            </SpecsGrid>

            {coupes.length > 0 && (
              <CoupeSection>
                <SectionTitle>Coupes disponibles</SectionTitle>
                <CoupeTags>
                  {coupes.map((c) => <CoupeTag key={c}>{c}</CoupeTag>)}
                </CoupeTags>
              </CoupeSection>
            )}

            {usages.length > 0 && (
              <UsageSection>
                <SectionTitle>Usages recommandés</SectionTitle>
                <UsageTags>
                  {usages.map((u) => <UsageTag key={u}>{u}</UsageTag>)}
                </UsageTags>
              </UsageSection>
            )}
          </Left>

          {/* ── DROITE sticky ── */}
          <Right>
            <CtaCard>
              <CtaTitle>Intéressé par ce produit ?</CtaTitle>
              <CtaDesc>
                Demandez un devis gratuit en 2 minutes. Réponse garantie sous 24h ouvrées.
              </CtaDesc>
              <CtaBtn to={`/devis?produit=${encodeURIComponent(p.name)}`}>
                Demander un devis gratuit →
              </CtaBtn>
              <CtaSecondary to="/produits">
                ← Voir tous les produits
              </CtaSecondary>
              <CtaDivider />
              <CtaInfoRow>
                <CtaInfoIcon>🚛</CtaInfoIcon>
                Livraison directe en Suisse
              </CtaInfoRow>
              <CtaInfoRow>
                <CtaInfoIcon>🇫🇷</CtaInfoIcon>
                Origine France garantie
              </CtaInfoRow>
              <CtaInfoRow>
                <CtaInfoIcon>📋</CtaInfoIcon>
                Ticket de pesée fourni
              </CtaInfoRow>
              <CtaInfoRow>
                <CtaInfoIcon>⚡</CtaInfoIcon>
                Réponse sous 24h ouvrées
              </CtaInfoRow>
            </CtaCard>
          </Right>
        </Layout>
      </PageWrap>
    </>
  )
}

export default ProduitDetail
